import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import { siteFields } from 'utils/SiteFormFields';
import {
  days,
  everyDaySchedule,
  intervalOptionsNumber,
  repeatsEveryOptions,
  rescheduleWeekendsOptions,
  rescheduleInvalidDates,
  numberArray,
  dayOfMonthOptions,
} from '../../../../mocks/utils';
import { TaskInfo } from '../../../../utils/constants/informationText';
import { SCHEDULE_PATTERNS } from '../../../../utils/constants/schedule';
import { optionalFormField } from '../../../../utils/methods/helpers';
import timezones from 'static/timezones.json';
import { TASK_SCHEDULE_LABELS } from 'utils/constants/task';

export const SCHEDULE = {
  startTime: ({ disabled, required, ...props } = {}) => ({
    field: 'startAfter',
    label: TASK_SCHEDULE_LABELS.startTime,
    type: 'datetime',
    size: 'half',
    disabled,
    required,
    minDate: dayjs(),
    ...props,
  }),
  startDate: ({ disabled, required, ...props } = {}) => ({
    field: 'startDate',
    label: TASK_SCHEDULE_LABELS.startDate,
    type: 'date-picker',
    size: 'half',
    disabled,
    required,
    minDate: dayjs(),
    ...props,
  }),
  repeatUntil: ({ disabled, required, ...props } = {}) => ({
    field: 'repeatUntil',
    label: TASK_SCHEDULE_LABELS.repeatUntil,
    type: 'date-picker',
    size: 'half',
    disabled,
    required,
    minDate: dayjs(),
    ...props,
  }),
  endTime: ({ disabled, required, ...props } = {}) => ({
    field: 'finishBefore',
    label: TASK_SCHEDULE_LABELS.finishTime,
    type: 'datetime',
    size: 'half',
    disabled,
    required,
    minDate: dayjs(),
    ...props,
  }),
  startTimeOnly: ({ disabled, required, ...props } = {}) => ({
    field: 'startTimeOnly',
    label: TASK_SCHEDULE_LABELS.startTime,
    type: 'time',
    size: 'half',
    disabled,
    required,
    ...props,
  }),
  endTimeOnly: ({ disabled, required, ...props } = {}) => ({
    field: 'endTimeOnly',
    label: TASK_SCHEDULE_LABELS.finishTime,
    type: 'time',
    size: 'half',
    info: TaskInfo.endTimeOnly,
    disabled,
    required,
    ...props,
  }),
  expectedDuration: ({ required } = {}) => ({
    id: 'expectedDuration',
    field: 'expectedDuration',
    label: TASK_SCHEDULE_LABELS.expectedDuration,
    fieldType: 'number',
    size: 'full',
    type: 'text',
    placeholder: 'Expected time in minutes e.g. 10',
    info: TaskInfo.duration,
    required,
  }),
  interval: ({ disabled, required = false, label } = {}) => ({
    field: 'interval',
    label: label ? label : TASK_SCHEDULE_LABELS.interval,
    size: 'half',
    type: 'select',
    placeholder: 'How often this task is scheduled',
    info: TaskInfo.interval,
    options: intervalOptionsNumber,
    required,
    disabled,
  }),
  repeatsEvery: ({ disabled, required, weekOrMonth, number } = {}) => ({
    field: 'repeatsEvery',
    label: TASK_SCHEDULE_LABELS.repeatsEvery,
    size: 'half',
    type: 'select',
    options: repeatsEveryOptions(weekOrMonth, number),
    required,
    disabled,
  }),
  everyday: {
    field: 'everyday',
    label: TASK_SCHEDULE_LABELS.everyDay,
    type: 'toggle',
    size: 'third',
    disable: false,
    updateDependentField: [
      {
        key: 'schedule.weekly.days',
        value: everyDaySchedule,
      },
    ],
    info: TaskInfo.everyDay,
  },
  days: ({ disabled } = {}) => ({
    field: 'days',
    label: TASK_SCHEDULE_LABELS.days,
    type: 'checkbox-options',
    options: days,
    size: 'full',
    disabled,
    info: TaskInfo.days,
  }),
  allDay: ({ disabled } = {}) => ({
    field: 'allDay',
    label: TASK_SCHEDULE_LABELS.allDay,
    type: 'toggle',
    disabled,
    size: 'full',
    updateDependentField: [
      { key: 'schedule.weekly.startTimeOnly', value: '00:00' },
      { key: 'schedule.weekly.endTimeOnly', value: '23:59' },
      { key: 'schedule.dateOfMonth.startTimeOnly', value: '00:00' },
      { key: 'schedule.dateOfMonth.endTimeOnly', value: '23:59' },
      { key: 'schedule.dayOfMonth.startTimeOnly', value: '00:00' },
      { key: 'schedule.dayOfMonth.endTimeOnly', value: '23:59' },
    ],
    info: TaskInfo.allDay,
  }),
  addSchedule: ({ disabled = false } = {}) => ({
    field: 'addSchedule',
    type: 'toggle',
    size: 'fifth',
    disabled,
  }),
  rescheduleWeekends: ({ disabled, required = true, label }) => ({
    field: 'rescheduleWeekends',
    label: label || TASK_SCHEDULE_LABELS.rescheduleWeekends,
    size: 'half',
    type: 'select',
    options: rescheduleWeekendsOptions,
    info: TaskInfo.rescheduleWeekends,
    required,
    disabled,
  }),
  rescheduleInvalidDates: ({ label, disabled, required, ...props }) => ({
    field: 'rescheduleInvalidDates',
    label: label || TASK_SCHEDULE_LABELS.rescheduleInvalidDates,
    size: 'half',
    type: 'select',
    info: TaskInfo.rescheduleInvalidDates,
    options: rescheduleInvalidDates,
    required,
    disabled,
    props,
  }),
  dateOfMonth: ({ disabled, required, ...props } = {}) => ({
    field: 'dateOfMonth',
    label: TASK_SCHEDULE_LABELS.dateOfMonth,
    type: 'select',
    size: 'half',
    disabled,
    options: numberArray(31),
    required,
    ...props,
  }),
  dayOfMonth: ({ disabled, required, ...props } = {}) => ({
    field: 'dayOfMonth',
    label: TASK_SCHEDULE_LABELS.dayOfMonth,
    type: 'select',
    size: 'half',
    disabled,
    options: dayOfMonthOptions,
    required,
    ...props,
  }),
};

const scheduleFields = {
  [SCHEDULE_PATTERNS.onceOff.id]: ({ timezone, disabled }) => {
    return {
      id: SCHEDULE_PATTERNS.onceOff.id,
      label: SCHEDULE_PATTERNS.onceOff.label,
      initialValues: { timezone: timezone },
      fields: [
        SCHEDULE.startTime({
          required: true,
          timeInterval: 1,
          maxMinuteInput: 59,
          disabled,
        }),
        SCHEDULE.endTime({
          required: false,
          timeInterval: 1,
          maxMinuteInput: 59,
          disabled,
        }),
        siteFields.timezone({
          timezones,
          label: TASK_SCHEDULE_LABELS.timezone,
          disabled,
        }),
      ],
    };
  },
  [SCHEDULE_PATTERNS.simpleInterval.id]: ({ timezone }) => ({
    id: SCHEDULE_PATTERNS.simpleInterval.id,
    label: SCHEDULE_PATTERNS.simpleInterval.label,
    initialValues: { timezone: timezone },
    fields: [
      SCHEDULE.startTime({
        required: true,
        timeInterval: 1,
        maxMinuteInput: 59,
      }),
      SCHEDULE.endTime({ required: true, timeInterval: 1, maxMinuteInput: 59 }),
      SCHEDULE.interval({ required: true }),
      siteFields.timezone({ timezones, label: TASK_SCHEDULE_LABELS.timezone }),
    ],
  }),
  [SCHEDULE_PATTERNS.weekly.id]: ({
    isDaysDisabled,
    isTimeOnlydisabled,
    isIntervalDisabled,
    timezone,
    disableAllDay,
  }) => ({
    id: SCHEDULE_PATTERNS.weekly.id,
    label: SCHEDULE_PATTERNS.weekly.label,
    initialValues: {
      everyday: false,
      allDay: false,
      timezone: timezone,
      repeatsEvery: 1,
    },
    fields: [
      SCHEDULE.startDate({ required: true }),
      SCHEDULE.repeatUntil({ required: false }),
      SCHEDULE.repeatsEvery({
        required: true,
        weekOrMonth: 'Week',
        number: 52,
      }),
      {
        // hidden element to create space
        field: '',
        label: '',
        type: 'toggle',
        size: 'third',
        visibility: 'hidden',
      },
      SCHEDULE.everyday,
      SCHEDULE.days({ required: true, disabled: isDaysDisabled }),
      SCHEDULE.allDay({ disabled: disableAllDay }),
      SCHEDULE.startTimeOnly({
        required: true,
        disabled: isTimeOnlydisabled,
        timeInterval: 1,
        maxMinuteInput: 59,
      }),
      SCHEDULE.endTimeOnly({
        required: true,
        disabled: isTimeOnlydisabled,
        timeInterval: 1,
        maxMinuteInput: 59,
      }),
      SCHEDULE.interval({
        disabled: isIntervalDisabled,
        label: TASK_SCHEDULE_LABELS.dailyInterval,
      }),
      siteFields.timezone({ timezones, label: TASK_SCHEDULE_LABELS.timezone }),
    ],
  }),
  [SCHEDULE_PATTERNS.dateOfMonth.id]: ({ timezone, isTimeEnabled }) => ({
    id: SCHEDULE_PATTERNS.dateOfMonth.id,
    label: SCHEDULE_PATTERNS.dateOfMonth.label,
    initialValues: {
      repeatsEvery: 1,
      timezone: timezone,
      rescheduleWeekends: 'NO',
      rescheduleInvalidDates: 'SKIP',
    },
    fields: [
      SCHEDULE.startDate({ required: true }),
      SCHEDULE.repeatUntil({ required: false }),
      SCHEDULE.repeatsEvery({
        weekOrMonth: 'Month',
        number: 36,
        required: true,
      }),
      siteFields.timezone({ timezones, label: TASK_SCHEDULE_LABELS.timezone }),
      SCHEDULE.rescheduleWeekends({
        required: true,
        disabled: false,
      }),
      SCHEDULE.rescheduleInvalidDates({
        required: true,
        disabled: false,
      }),
      SCHEDULE.dateOfMonth({ required: true }),

      SCHEDULE.allDay(),
      SCHEDULE.startTimeOnly({
        required: true,
        disabled: isTimeEnabled,
        timeInterval: 1,
        maxMinuteInput: 59,
      }),
      SCHEDULE.endTimeOnly({
        required: true,
        disabled: isTimeEnabled,
        timeInterval: 1,
        maxMinuteInput: 59,
      }),
      SCHEDULE.interval({
        disabled: false,
        label: TASK_SCHEDULE_LABELS.dailyInterval,
      }),
    ],
  }),

  [SCHEDULE_PATTERNS.dayOfMonth.id]: ({ timezone, isDayTimeEnabled }) => ({
    id: SCHEDULE_PATTERNS.dayOfMonth.id,
    label: SCHEDULE_PATTERNS.dayOfMonth.label,
    initialValues: { repeatsEvery: 1, timezone: timezone },
    fields: [
      SCHEDULE.startDate({ required: true }),
      SCHEDULE.repeatUntil({ required: false }),
      SCHEDULE.repeatsEvery({
        weekOrMonth: 'Month',
        number: 36,
        required: true,
      }),
      SCHEDULE.dayOfMonth({ required: true }),
      SCHEDULE.days({ required: true, disabled: false }),
      siteFields.timezone({ timezones, label: TASK_SCHEDULE_LABELS.timezone }),
      SCHEDULE.allDay(),
      SCHEDULE.startTimeOnly({
        required: true,
        disabled: isDayTimeEnabled,
        timeInterval: 1,
        maxMinuteInput: 59,
      }),
      SCHEDULE.endTimeOnly({
        required: true,
        disabled: isDayTimeEnabled,
        timeInterval: 1,
        maxMinuteInput: 59,
      }),
      SCHEDULE.interval({
        disabled: false,
        label: TASK_SCHEDULE_LABELS.dailyInterval,
      }),
    ],
  }),
};

export const fields = ({
  field = 'schedule',
  size = 'full',
  activeTab = SCHEDULE_PATTERNS.onceOff.label,
  values,
  displayView = [],
  formItem,
  editMode,
  initialValues,
  disabled = false,
} = {}) => {
  // Weekly calculations
  const isWeekly =
    Boolean(values.schedule) &&
    values.schedule.activeTab === SCHEDULE_PATTERNS.weekly.label;

  const isDaysDisabled = isWeekly ? values.schedule?.weekly?.everyday : false;
  //Times should be disabled if there are no days selected or allDay is toggled to true
  const isTimeOnlydisabled = isWeekly
    ? isEmpty(values.schedule?.weekly?.days) || values.schedule?.weekly?.allDay
    : false;
  //AllDay toggle should be disabled if there are no days selected
  const disableAllDay = isWeekly
    ? isEmpty(values.schedule?.weekly?.days)
    : false;

  const isIntervalDisabled = isWeekly
    ? isEmpty(values.schedule?.weekly?.days)
    : false;

  const hideTabs = formItem.hideAddSchedule
    ? false
    : Boolean(!values.addSchedule);
  //-------------------------------------------------------------------------end---------------------------
  const timezone = initialValues?.timezone ? initialValues?.timezone : false;

  // date of month calculations
  const isDateOfMonth =
    Boolean(values.schedule) &&
    values.schedule.activeTab === SCHEDULE_PATTERNS.dateOfMonth.label;

  const isTimeEnabled = isDateOfMonth
    ? values.schedule?.dateOfMonth?.allDay
    : false;
  /// --------------------------------------------------------------------end---------------------------

  const isDayOfMonth =
    Boolean(values.schedule) &&
    values.schedule.activeTab === SCHEDULE_PATTERNS.dayOfMonth.label;

  const isDayTimeEnabled = isDayOfMonth
    ? values.schedule?.dayOfMonth?.allDay
    : false;
  return [
    ...optionalFormField({
      showWhen: !formItem.hideAddSchedule,
      field: SCHEDULE.addSchedule({
        disabled: (editMode && !isEmpty(initialValues.schedule)) || disabled,
      }),
    }),
    {
      type: 'form-tabs',
      activeTab,
      field,
      size,
      hideTabsOnly: true,
      hidden: hideTabs,
      tabs: isEmpty(displayView)
        ? [
            scheduleFields[SCHEDULE_PATTERNS.onceOff.id]({
              timezone,
            }),
            scheduleFields[SCHEDULE_PATTERNS.simpleInterval.id]({
              timezone,
            }),
            scheduleFields[SCHEDULE_PATTERNS.weekly.id]({
              isDaysDisabled,
              isTimeOnlydisabled,
              isIntervalDisabled,
              timezone,
              disableAllDay,
            }),
            scheduleFields[SCHEDULE_PATTERNS.dateOfMonth.id]({
              timezone,
              isTimeEnabled,
            }),
            scheduleFields[SCHEDULE_PATTERNS.dayOfMonth.id]({
              timezone,
              isDayTimeEnabled,
            }),
          ]
        : displayView.map(item => {
            return scheduleFields[item]({ timezone, disabled });
          }),
    },
  ];
};
